import { useQuery } from "@apollo/client";
import { useState } from "react";
import {
  AssessmentQuestionComment,
  ComplianceProgramStep,
  GetAssessmentGroupForSidebarQuery,
  GetAssessmentGroupForSidebarQueryVariables,
  GetReviewPreviewPersonQueryQuery,
  GetReviewPreviewPersonQueryQueryVariables,
  GetReviewStepQuestionsQuery,
  GetReviewStepQuestionsQueryVariables,
  GoalState,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import AssessmentQuestionMultiSelectOptions from "@apps/assessments/components/assessment-question-multi-select-options";
import AssessmentQuestionRangeOptions from "@apps/assessments/components/assessment-question-range-options";
import getAssessmentGroupForSidebar from "@apps/programs/graphql/get-assessment-group-for-sidebar-query";
import getReviewPreviewPersonQuery from "@apps/programs/graphql/get-review-preview-person-query";
import getReviewStepQuestionsQuery from "@apps/programs/graphql/get-review-step-questions-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { ToggleButtonGroup } from "@components/toggle-button-group/toggle-button-group";
import { ToggleButtonGroupTheme } from "@components/toggle-button-group/toggle-button-group-constants";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import {
  findReviewStepTemplate,
  getReviewQuestionScope,
  reviewPreviewChanges,
} from "../review-questions/review-question-rows";
import {
  ProgramForm,
  ProgramFormStepDownwardReview,
  ProgramFormStepPeerReview,
} from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import {
  PreviewQuestion,
  PreviewReviewer,
  PreviewSection,
  buildPreviewSections,
  previewGoalPeriod,
} from "./review-preview-questions";
import { useCoreValues } from "./system-question-summaries";

// Beside the Questions tab's configuration, or under it on narrow screens:
// every question one kind of reviewer would be asked, laid out as in the
// review, so admins can check the whole review before publishing. Picking a
// person swaps the role and goal examples for that person's own.

type Description = PreviewQuestion["description"];

const PreviewDescription = ({ description }: { description: Description }) => {
  if (!description) {
    return null;
  }
  if ("plain" in description) {
    return (
      <div className="whitespace-pre-line text-sm text-gray-500">
        {description.plain}
      </div>
    );
  }
  return isEmptyValue(description.rich) ? null : (
    <TextareaWysiwyg
      className="text-gray-500"
      value={description.rich}
      fullWidth
    />
  );
};

// Stands in for a text box the reviewer would type into.
const PreviewTextBox = ({ placeholder }: { placeholder: string }) => (
  <div className="min-h-16 rounded-md border bg-white px-3 py-2 text-sm text-gray-400">
    {placeholder}
  </div>
);

const PreviewQuestionCard = ({ question }: { question: PreviewQuestion }) => {
  const { input } = question;
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-white p-6">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 empty:hidden">
          {question.title && (
            <div className="tf-header-base-dark">{question.title}</div>
          )}
          {question.example && (
            <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
              Example
            </span>
          )}
        </div>
        <PreviewDescription description={question.description} />
      </div>
      {input.kind === "scale" && (
        <AssessmentQuestionRangeOptions
          disabled
          startValue={input.startValue}
          endValue={input.endValue}
          labels={input.labels}
          labelDescriptions={input.labelDescriptions}
        />
      )}
      {input.kind === "choices" && (
        <AssessmentQuestionMultiSelectOptions
          disabled
          options={input.options}
          optionDescriptions={input.optionDescriptions}
        />
      )}
      {input.kind === "text" ? (
        <PreviewTextBox placeholder="Write an answer..." />
      ) : (
        question.comment !== AssessmentQuestionComment.NoComment && (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold uppercase text-gray-500">
              Comment
              {question.comment === AssessmentQuestionComment.Required && " *"}
            </div>
            <PreviewTextBox placeholder="Add a comment..." />
          </div>
        )
      )}
    </div>
  );
};

const PreviewSectionView = ({ section }: { section: PreviewSection }) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-1">
      {section.title && <h3 className="tf-header-sm-dark">{section.title}</h3>}
      <PreviewDescription description={section.description} />
      {section.note && <p className="text-sm text-gray-500">{section.note}</p>}
    </div>
    {section.questions.map((question) => (
      <PreviewQuestionCard key={question.key} question={question} />
    ))}
  </div>
);

const ReviewPreview = ({
  proposedComplianceProgram,
  publishedReviewId,
  talentIndicatorData = [],
  className,
}: {
  proposedComplianceProgram: ProgramForm;
  // Set on a published review, whose own question changes the preview shows.
  publishedReviewId: number | null;
  className?: string;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const coreValues = useCoreValues();

  const { steps } = proposedComplianceProgram;
  const hasSelfReview = steps.some(
    ({ stepType }) => stepType === ComplianceProgramStep.SelfReview,
  );
  const downwardReviewStep = steps.find(
    (step): step is ProgramFormStepDownwardReview =>
      step.stepType === ComplianceProgramStep.DownwardReview,
  );
  const peerReviewStep = steps.find(
    (step): step is ProgramFormStepPeerReview =>
      step.stepType === ComplianceProgramStep.PeerReview,
  );
  // Matches when the Questions tab shows each review type above.
  const reviewers = [
    ...(downwardReviewStep && hasSelfReview
      ? [PreviewReviewer.Manager, PreviewReviewer.Self]
      : []),
    ...(peerReviewStep ? [PreviewReviewer.Peer] : []),
  ];

  const [chosenReviewer, setChosenReviewer] = useState<PreviewReviewer>();
  const [person, setPerson] = useState<UserComboboxUserOption | null>(null);
  const userComboboxQuery = useUserComboboxQuery({ selected: person });
  const reviewer =
    chosenReviewer && reviewers.includes(chosenReviewer)
      ? chosenReviewer
      : reviewers[0];
  const step =
    reviewer === PreviewReviewer.Peer ? peerReviewStep : downwardReviewStep;
  const questionSetId = step?.questionSetIds[0];

  // The question set drawer runs the same query, so this is often cached.
  const { data, loading } = useQuery<
    GetAssessmentGroupForSidebarQuery,
    GetAssessmentGroupForSidebarQueryVariables
  >(getAssessmentGroupForSidebar, {
    skip: !questionSetId,
    variables: { assessmentGroupId: questionSetId ?? 0 },
  });

  // A published review can remove, hide or reword questions (TF-1697). The
  // Questions panel runs the same query and refetches it after each change.
  const review = getReviewQuestionScope(publishedReviewId, step);
  const { data: reviewData, loading: loadingReview } = useQuery<
    GetReviewStepQuestionsQuery,
    GetReviewStepQuestionsQueryVariables
  >(getReviewStepQuestionsQuery, {
    skip: !review,
    variables: { complianceProgramId: review?.complianceProgramId ?? 0 },
  });
  const reviewTemplate = review
    ? findReviewStepTemplate(
        reviewData?.complianceProgram,
        review.assessmentTemplateId,
      )
    : null;

  const {
    data: personData,
    loading: loadingPerson,
    error: personError,
  } = useQuery<
    GetReviewPreviewPersonQueryQuery,
    GetReviewPreviewPersonQueryQueryVariables
  >(getReviewPreviewPersonQuery, {
    skip: !person,
    variables: {
      userId: person?.id ?? 0,
      // The review leaves out draft goals.
      goalStates: [GoalState.Open, GoalState.Closed],
    },
  });

  if (!reviewer || !step) {
    return null;
  }

  const sections = buildPreviewSections({
    reviewer,
    questionSet: questionSetId ? data?.assessmentGroup : null,
    roleBasedReviewConfig: step.roleBasedReviewConfig,
    goalReviewConfig: step.goalReviewConfig,
    coreValueReviewConfig: step.coreValueReviewConfig,
    talentIndicatorReviewConfig:
      step.stepType === ComplianceProgramStep.DownwardReview
        ? step.talentIndicatorReviewConfig
        : undefined,
    talentIndicators: talentIndicatorData,
    coreValues,
    hasGoals: !!organization.featureFlags.goals,
    person: person ? personData?.user : null,
    goalPeriod: previewGoalPeriod(
      proposedComplianceProgram,
      personData?.user?.organizationSpecificInfo?.startDate,
    ),
    label,
    reviewChanges: reviewTemplate ? reviewPreviewChanges(reviewTemplate) : null,
  });

  return (
    <ComplianceProgramFormStep
      title="Question Preview"
      className={className}
      scrollable
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <UserCombobox
            aria-label="Person being reviewed"
            containerClassName="w-56"
            placeholder="Any person"
            clearable
            buttonProps={{ size: ButtonSize.medium }}
            loading={userComboboxQuery.loading}
            query={userComboboxQuery.query}
            value={person}
            options={userComboboxQuery.options}
            onChangeValue={(option) => {
              if (option.type === UserComboboxOptionType.USER) {
                setPerson(option);
              }
            }}
            onChangeQuery={userComboboxQuery.setQuery}
            onClearValue={() => setPerson(null)}
          />
          <ToggleButtonGroup
            aria-label="Preview as"
            theme={ToggleButtonGroupTheme.defaultButtons}
            size={ButtonSize.medium}
            buttons={reviewers.map((value) => ({
              title: value,
              value,
              active: value === reviewer,
            }))}
            onClick={({ value }) => setChosenReviewer(value)}
          />
        </div>
      }
    >
      {personError && person && (
        // Otherwise the examples below would pass for this person's own.
        <p className="text-sm text-red-600">
          {`Could not load ${person.name}'s roles and ${label("goal", {
            pluralize: true,
          })}, so examples show instead.`}
        </p>
      )}
      {loading || loadingPerson || loadingReview ? (
        <Loading mini size={5} />
      ) : sections.length === 0 ? (
        <p className="text-sm text-gray-500">No questions yet.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {sections.map((section) => (
            <PreviewSectionView key={section.key} section={section} />
          ))}
        </div>
      )}
    </ComplianceProgramFormStep>
  );
};

export default ReviewPreview;
